import Image from 'next/image'
import clsx from 'clsx'

import Link from '@/atoms/Link/Link'
import Carousel from '@/atoms/Carousel/Carousel'
import RichTextContent from '@/templates/RichTextContent/RichTextContent'

/* assets */
import { ReactComponent as RightNavigator } from 'public/assets/icons/RightNavigator.svg'
import { ReactComponent as InstagramIcon } from 'public/assets/influencer/icons/social/Instagram.svg'
import { ReactComponent as TwitterIcon } from 'public/assets/influencer/icons/social/Twitter.svg'
import { ReactComponent as LinkedInIcon } from 'public/assets/influencer/icons/social/Linkedin.svg'
import { getFormatRgbaColor } from '@/utils/functions/utils'

/* styling */
import styles from './_.module.css'

interface Props {
  className?: string
  influencerTitle?: string
  featuredInfluencers: Influencer[]
}

function FeaturedInfluencer({
  className,
  featuredInfluencers,
  influencerTitle
}: Props): JSX.Element {
  return (
    <div className={className}>
      {influencerTitle ? (
        <h2 className="font-heading text-2xl md:text-3xl text-left md:text-center font-medium | max-w-4xl | mb-14 md:mb-20 mx-auto">
          {influencerTitle}
        </h2>
      ) : null}

      <Carousel
        className="overflow-hidden"
        sliderClassName="px-8 lg:px-16"
        navigationType="arrow"
        buttonConfig={{
          prevButtonClassName: 'left-0 | rounded-full',
          nextButtonClassName: 'right-0 | rounded-full'
        }}
        slides={featuredInfluencers.map(influencer => {
          return (
            <div key={influencer.slug} className="px-5 | text-center | group">
              <div className="md:flex shadow-md">
                <div className="md:shrink-0 overflow-hidden md:rounded-tl-md md:rounded-bl-md">
                  {influencer.profileImage.data && (
                    <Image
                      src={influencer.profileImage.data?.attributes.url}
                      className="h-[12rem] sm:h-[16rem]  md:h-[21.8rem] | w-full md:w-[24rem] lg:w-[27rem] | object-cover | rounded-tl-md rounded-tr-md md:rounded-tl-md md:rounded-bl-md md:rounded-tr-none | md:group-hover:scale-105 transition-transform ease-linear"
                      alt={
                        influencer.profileImage.data?.attributes
                          .alternativeText ?? ''
                      }
                      width={influencer.profileImage.data?.attributes.width}
                      height={influencer.profileImage.data?.attributes.height}
                      priority
                    />
                  )}
                </div>

                <div
                  className="p-3 md:p-5 lg:p-10 | md:text-left | !text-black | flex flex-col justify-between grow rounded-bl-md rounded-br-md md:rounded-tr-md md:rounded-br-md md:rounded-bl-none"
                  style={{
                    backgroundColor: getFormatRgbaColor(influencer.themeColor)
                  }}
                >
                  <div className="text-left | h-[10rem] md:h-auto">
                    <p className="text-xl md:text-4xl font-heading font-medium | mb-1 | line-clamp-1">
                      {influencer.name}
                    </p>
                    <p className="text-xs md:text-lg | mb-3 md:mb-7 | line-clamp-2">
                      {influencer.designation}
                    </p>
                    <RichTextContent
                      className={clsx(
                        styles.featured_influencer,
                        'break-words line-clamp-4 | text-xs md:text-base'
                      )}
                      content={influencer.description}
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <Link
                      href={`/global-marketing-leaders/${influencer.slug}/`}
                      className="font-medium text-xs md:text-base | flex items-center gap-0.5 | text-blue-50"
                    >
                      Read More
                      <RightNavigator
                        aria-hidden
                        className="h-2 w-2 lg:h-3 lg:w-3 | inline | ml-0.5 | !fill-blue-50"
                      />
                    </Link>
                    <div className={clsx('flex | gap-2 lg:gap-4')}>
                      {influencer.instagramLink && (
                        <Link
                          href={influencer.instagramLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <InstagramIcon
                            color="#313131"
                            className="h-3 w-3 lg:h-5 lg:w-5"
                          />
                        </Link>
                      )}
                      {influencer.twitterLink && (
                        <Link
                          href={influencer.twitterLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <TwitterIcon
                            color="#313131"
                            className="h-3 w-3 lg:h-5 lg:w-5"
                          />
                        </Link>
                      )}
                      {influencer.linkedinLink && (
                        <Link
                          href={influencer.linkedinLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <LinkedInIcon
                            color="#313131"
                            className="h-3 w-3 lg:h-5 lg:w-5"
                          />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      />
    </div>
  )
}

export default FeaturedInfluencer
